import React from 'react';
import {
  Grid,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  MenuItem,
  Select,
  SelectChangeEvent,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Box,
  Collapse,
  Alert,
} from '@mui/material';
import {
  Hotel,
  DirectionsCar,
  AccountBalance,
  People,
  CheckCircle,
} from '@mui/icons-material';
import { BandRecommendationFormData } from '../../types/BandRecommendations';

interface FinancialInfoProps {
  formData: BandRecommendationFormData;
  setFormData: React.Dispatch<React.SetStateAction<BandRecommendationFormData>>;
}

const currencies = [
  { code: 'EUR', label: 'Euro (€)',          symbol: '€'   },
  { code: 'USD', label: 'US Dollar ($)',      symbol: '$'   },
  { code: 'GBP', label: 'British Pound (£)', symbol: '£'   },
  { code: 'CHF', label: 'Swiss Franc',        symbol: 'CHF' },
  { code: 'NOK', label: 'Norwegian Krone',    symbol: 'kr'  },
  { code: 'SEK', label: 'Swedish Krona',      symbol: 'kr'  },
  { code: 'DKK', label: 'Danish Krone',       symbol: 'kr'  },
];

const accommodationOptions = [
  { value: 'single_rooms',      label: 'Individual single rooms'         },
  { value: 'shared_rooms',      label: 'Shared double rooms'             },
  { value: 'mixed_arrangement', label: 'Mixed arrangement'               },
  { value: 'band_preference',   label: 'Band will arrange accommodation' },
];

const transportOptions = [
  { value: 'car',                label: 'Standard car'             },
  { value: 'large_car',          label: 'Large car / estate'       },
  { value: 'van',                label: 'Van / minibus'            },
  { value: 'provided_transport', label: 'Venue provides transport' },
  { value: 'own_transport',      label: 'Band has own transport'   },
];

const FinancialInfo: React.FC<FinancialInfoProps> = ({ formData, setFormData }) => {

  const financialDetails       = formData.financialDetails || {};
  const selectedCurrency       = currencies.find(c => c.code === financialDetails.preferredCurrency) || currencies[0];
  const maleMembers            = Number(formData.maleMembers)  || 0;
  const femaleMembers          = Number(formData.femaleMembers) || 0;
  const totalMembers           = maleMembers + femaleMembers;
  const hasGenderMix           = maleMembers > 0 && femaleMembers > 0;
  const feeToBeConfirmed       = financialDetails.feeToBeConfirmed       ?? false;
  const logisticsToBeConfirmed = financialDetails.logisticsToBeConfirmed ?? false;
  const accommodationNeeded    = financialDetails.accommodationNeeded    ?? false;
  const transportationNeeded   = financialDetails.transportationNeeded   ?? false;
  const accDetails             = financialDetails.accommodationDetails   || {};
  const transDetails           = financialDetails.transportDetails       || {};

  const handleTopLevelChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = event.target;
    setFormData(prevData => ({
      ...prevData,
      financialDetails: {
        ...prevData.financialDetails,
        [name]: type === 'checkbox' ? checked : type === 'number' ? Number(value) : value,
      },
    }));
  };

  const handleCurrencyChange = (event: SelectChangeEvent<string>) => {
    setFormData(prevData => ({
      ...prevData,
      financialDetails: { ...prevData.financialDetails, preferredCurrency: event.target.value },
    }));
  };

  const setFinancialField = (field: string, value: any) => {
    setFormData(prevData => ({
      ...prevData,
      financialDetails: { ...prevData.financialDetails, [field]: value },
    }));
  };

  const handleAccommodationDetailChange = (field: string, value: any) => {
    setFormData(prevData => ({
      ...prevData,
      financialDetails: {
        ...prevData.financialDetails,
        accommodationDetails: { ...(prevData.financialDetails?.accommodationDetails || {}), [field]: value },
      },
    }));
  };

  const handleTransportDetailChange = (field: string, value: any) => {
    setFormData(prevData => ({
      ...prevData,
      financialDetails: {
        ...prevData.financialDetails,
        transportDetails: { ...(prevData.financialDetails?.transportDetails || {}), [field]: value },
      },
    }));
  };

  const getAccommodationSuggestion = () => {
    if (totalMembers === 0) return 'Complete band information first';
    if (totalMembers === 1) return '1 single room';
    if (totalMembers === 2 && !hasGenderMix) return '1 double room or 2 singles';
    if (totalMembers === 2 && hasGenderMix) return '2 single rooms recommended';
    if (hasGenderMix) return `${Math.ceil(totalMembers / 2)} rooms minimum (mixed gender band)`;
    return `${Math.ceil(totalMembers / 2)} rooms for ${totalMembers} members`;
  };

  const getTransportSuggestion = () => {
    if (totalMembers === 0) return 'Complete band information first';
    if (totalMembers <= 2) return 'Standard car suitable';
    if (totalMembers <= 4) return 'Large car or small van';
    return 'Van or minibus required';
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>

      {/* Fee */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccountBalance color="primary" />
            Performance Fee
          </Typography>

          <Typography variant="body2" color="text.secondary" gutterBottom>
            Set the minimum performance fee and preferred currency. If you don't know the fee
            right now, tick the box below and it can be confirmed directly with the booking contact.
          </Typography>

          <Box sx={{
            mt: 2, mb: 3, p: 2,
            border: '1px solid',
            borderColor: feeToBeConfirmed ? 'success.main' : 'divider',
            borderRadius: 1,
            bgcolor: feeToBeConfirmed ? 'success.light' : 'background.paper',
            transition: 'all 0.2s',
          }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={feeToBeConfirmed}
                  onChange={handleTopLevelChange}
                  name="feeToBeConfirmed"
                  color="success"
                />
              }
              label={
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>Fee to be confirmed</Typography>
                  <Typography variant="body2" color="text.secondary">
                    The fee will be agreed directly between the programmer and the booking contact.
                  </Typography>
                </Box>
              }
            />
          </Box>

          {feeToBeConfirmed && (
            <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 2 }}>
              Noted. The fee field will be left blank and confirmed at booking stage.
            </Alert>
          )}

          <Collapse in={!feeToBeConfirmed}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="minimumFee"
                  label="Minimum Performance Fee"
                  type="number"
                  value={financialDetails.minimumFee ?? ''}
                  onChange={handleTopLevelChange}
                  variant="outlined"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography variant="body2" sx={{ minWidth: 20 }}>{selectedCurrency.symbol}</Typography>
                      </InputAdornment>
                    ),
                    inputProps: { min: 0, step: 50 }
                  }}
                  placeholder="0"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Preferred Currency</InputLabel>
                  <Select
                    value={financialDetails.preferredCurrency ?? 'EUR'}
                    onChange={handleCurrencyChange}
                    label="Preferred Currency"
                  >
                    {currencies.map((currency) => (
                      <MenuItem key={currency.code} value={currency.code}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ minWidth: 30 }}>{currency.symbol}</Typography>
                          {currency.label}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Collapse>
        </CardContent>
      </Card>

      {/* Single checkbox to skip both accommodation and transport */}
      <Box sx={{
        mb: 3, p: 2,
        border: '1px solid',
        borderColor: logisticsToBeConfirmed ? 'success.main' : 'divider',
        borderRadius: 1,
        bgcolor: logisticsToBeConfirmed ? 'success.light' : 'background.paper',
        transition: 'all 0.2s',
      }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={logisticsToBeConfirmed}
              onChange={(e) => setFinancialField('logisticsToBeConfirmed', e.target.checked)}
              color="success"
            />
          }
          label={
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>Logistics to be confirmed</Typography>
              <Typography variant="body2" color="text.secondary">
                Accommodation and transport will be agreed directly with the booking contact.
              </Typography>
            </Box>
          }
        />
        {logisticsToBeConfirmed && (
          <Alert severity="success" icon={<CheckCircle />} sx={{ mt: 1 }}>
            Noted. Both sections will be left blank and confirmed at booking stage.
          </Alert>
        )}
      </Box>

      {/* Accommodation and transport — both collapsed by the single checkbox above */}
      <Collapse in={!logisticsToBeConfirmed}>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Hotel color="primary" />
              Accommodation Requirements
            </Typography>

            {totalMembers > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <People fontSize="small" />
                  <Typography variant="body2">
                    <strong>{totalMembers} band members</strong> ({maleMembers}M, {femaleMembers}F) —
                    suggestion: {getAccommodationSuggestion()}
                  </Typography>
                </Box>
              </Alert>
            )}

            <FormControlLabel
              control={
                <Checkbox
                  checked={accommodationNeeded}
                  onChange={handleTopLevelChange}
                  name="accommodationNeeded"
                />
              }
              label="Accommodation needed for band"
              sx={{ mb: 2 }}
            />

            <Collapse in={accommodationNeeded}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Accommodation Type</InputLabel>
                    <Select
                      value={accDetails.type || 'single_rooms'}
                      onChange={(e) => handleAccommodationDetailChange('type', e.target.value)}
                      label="Accommodation Type"
                    >
                      {accommodationOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Number of Nights"
                    type="number"
                    value={accDetails.nights || 1}
                    onChange={(e) => handleAccommodationDetailChange('nights', Number(e.target.value))}
                    InputProps={{ inputProps: { min: 1, max: 7 } }}
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Special Requirements"
                    value={accDetails.specialRequirements || ''}
                    onChange={(e) => handleAccommodationDetailChange('specialRequirements', e.target.value)}
                    placeholder="e.g. Ground floor rooms, pet-friendly, dietary requirements"
                    multiline
                    rows={2}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Collapse>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DirectionsCar color="primary" />
              Transportation Requirements
            </Typography>

            {totalMembers > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>For {totalMembers} people + equipment:</strong> {getTransportSuggestion()}
                </Typography>
              </Alert>
            )}

            <FormControlLabel
              control={
                <Checkbox
                  checked={transportationNeeded}
                  onChange={handleTopLevelChange}
                  name="transportationNeeded"
                />
              }
              label="Transportation assistance needed"
              sx={{ mb: 2 }}
            />

            <Collapse in={transportationNeeded}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Transport Type</InputLabel>
                    <Select
                      value={transDetails.type || 'car'}
                      onChange={(e) => handleTransportDetailChange('type', e.target.value)}
                      label="Transport Type"
                    >
                      {transportOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Maximum Travel Distance (km)"
                    type="number"
                    value={transDetails.distance || ''}
                    onChange={(e) => handleTransportDetailChange('distance', e.target.value)}
                    placeholder="e.g. 200"
                    InputProps={{ inputProps: { min: 0 } }}
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={transDetails.returnNeeded ?? true}
                        onChange={(e) => handleTransportDetailChange('returnNeeded', e.target.checked)}
                      />
                    }
                    label="Return transportation needed"
                  />
                </Grid>
              </Grid>
            </Collapse>
          </CardContent>
        </Card>

      </Collapse>

    </Box>
  );
};

export default FinancialInfo;